import type { RealtimeSessionUsage } from '../hooks/useOpenAIRealtime';

export type RealtimeProvider = 'gemini' | 'openai';

// 各 provider 的可选音色列表。新增 provider 时在此添加一组即可，admin 设置页会自动按此渲染。
// 注意：同时需要在 packages/app/locales/*/translations.json 的
//   realtime.voice_characteristics 下补充每个 voice 的特征文案，否则下拉项副标题会显示 key 原文。
export const REALTIME_VOICE_OPTIONS: Record<RealtimeProvider, { value: string; label: string }[]> = {
  gemini: [
    'Zephyr',
    'Puck',
    'Charon',
    'Kore',
    'Fenrir',
    'Leda',
    'Orus',
    'Aoede',
    'Callirrhoe',
    'Autonoe',
    'Enceladus',
    'Iapetus',
    'Umbriel',
    'Algieba',
    'Despina',
    'Erinome',
    'Algenib',
    'Rasalgethi',
    'Laomedeia',
    'Achernar',
    'Alnilam',
    'Schedar',
    'Gacrux',
    'Pulcherrima',
    'Achird',
    'Zubenelgenubi',
    'Vindemiatrix',
    'Sadachbia',
    'Sadaltager',
    'Sulafat',
  ].map((v) => ({ value: v, label: v })),
  openai: [
    { value: 'alloy', label: 'Alloy' },
    { value: 'ash', label: 'Ash' },
    { value: 'ballad', label: 'Ballad' },
    { value: 'cedar', label: 'Cedar' },
    { value: 'coral', label: 'Coral' },
    { value: 'echo', label: 'Echo' },
    { value: 'marin', label: 'Marin' },
    { value: 'sage', label: 'Sage' },
    { value: 'shimmer', label: 'Shimmer' },
    { value: 'verse', label: 'Verse' },
  ],
};

export const REALTIME_DEFAULT_VOICE_MAP: Record<RealtimeProvider, string> = {
  gemini: 'Zephyr',
  openai: 'alloy',
};

// OpenAI Realtime 模型版本。新增模型时只需在此追加一项，admin 下拉自动渲染。
export const OPENAI_REALTIME_MODELS: { value: string; label: string }[] = [
  { value: 'gpt-realtime', label: 'gpt-realtime' },
  { value: 'gpt-realtime-2', label: 'gpt-realtime-2' },
  { value: 'gpt-realtime-2.1', label: 'gpt-realtime-2.1' },
  { value: 'gpt-live-1', label: 'gpt-live-1' },
];

export const OPENAI_REALTIME_DEFAULT_MODEL = 'gpt-realtime';

// GPT-Live（全二重）模型：不是 Realtime 协议——useRealtime 分流到 useOpenAILive（realtime 服务 /openai-live/session 换 SDP，
// oai-events 上是 Live 事件，工具走 responses 委任，按秒计费）。不进 OPENAI_REALTIME_REASONING_MODELS → effort 自动隐藏。
// 白名单与 felo-mygpt enums.OPENAI_REALTIME_MODEL_ALLOWED 同步。
export const OPENAI_LIVE_MODELS = new Set(['gpt-live-1']);
export const OPENAI_LIVE_DEFAULT_MODEL = 'gpt-live-1';
export const isOpenAILiveModel = (model?: string | null): boolean => !!model && OPENAI_LIVE_MODELS.has(model);

// GPT-Live 额外音色；不适用于旧 Realtime API。与 AI 电话共用，保留原有 10 款基础音色。
// 官方列表：https://developers.openai.com/api/docs/guides/live-conversations#voice-options
export const OPENAI_LIVE_ADDITIONAL_VOICE_OPTIONS = [
  'quartz',
  'ripple',
  'vesper',
  'willow',
  'stone',
  'gleam',
  'meridian',
  'bossa',
  'tempo',
  'beacon',
  'delta',
  'cinder',
  'nuri',
  'juni',
  'flitz',
  'brise',
  'sillage',
  'nira',
  'harema',
  'shida',
  'noeul',
].map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }));

const OPENAI_LIVE_VOICE_OPTIONS = [...REALTIME_VOICE_OPTIONS.openai, ...OPENAI_LIVE_ADDITIONAL_VOICE_OPTIONS];

export const getRealtimeVoiceOptions = (provider: RealtimeProvider, model?: string | null) =>
  provider === 'openai' && isOpenAILiveModel(model) ? OPENAI_LIVE_VOICE_OPTIONS : REALTIME_VOICE_OPTIONS[provider];

// 模型切换/读取旧配置时，避免把 Live 专属音色传入 Realtime 会话。
// Web 保持 alloy 默认值，电话调用方显式传入 marin。
export const resolveRealtimeVoice = (
  provider: RealtimeProvider,
  model: string | null | undefined,
  voice: string | null | undefined,
  fallback = REALTIME_DEFAULT_VOICE_MAP[provider]
): string => (getRealtimeVoiceOptions(provider, model).some((option) => option.value === voice) ? voice! : fallback);

// gpt-live-1 声音提示词上限（与后端 OPENAI_LIVE_VOICE_PROMPT_MAX_LEN 同值）
export const OPENAI_LIVE_VOICE_PROMPT_MAX_LEN = 8000;

// 支持 reasoning.effort 配置的 OpenAI Realtime 模型（仅 v2 起）
export const OPENAI_REALTIME_REASONING_MODELS = new Set(['gpt-realtime-2', 'gpt-realtime-2.1']);

// gpt-realtime-2 的 reasoning.effort 取值（OpenAI 官方文档）
export const OPENAI_REASONING_EFFORT_VALUES = ['minimal', 'low', 'medium', 'high', 'xhigh'] as const;
export type OpenAIReasoningEffort = (typeof OPENAI_REASONING_EFFORT_VALUES)[number];

// 官方推荐生产语音 agent 起点
export const OPENAI_REASONING_EFFORT_DEFAULT: OpenAIReasoningEffort = 'low';

// Gemini Live 模型版本（bot 配置 gemini_realtime_model）。realtime 服务按此键选模型，前端不改 /live-stream 参数。
// 新增模型时只需在此追加一项，admin 下拉自动渲染；白名单/缺省与 felo-mygpt enums.GEMINI_REALTIME_MODEL_ALLOWED 保持一致。
export const GEMINI_REALTIME_MODELS: { value: string; label: string }[] = [
  { value: 'gemini-3.1-flash-live-preview', label: 'gemini-3.1-flash-live' },
  { value: 'gemini-3.8-live', label: 'gemini-3.8-live' },
];

export const GEMINI_REALTIME_DEFAULT_MODEL = 'gemini-3.1-flash-live-preview';

// 支持思考程度（voice_thinking_level）的 Gemini 模型白名单；gemini-3.8-live 是固定档 interleaved reasoning，
// setup 不接受 thinkingLevel，admin 对其隐藏该项（realtime 侧同样按模型名省略）。
export const GEMINI_THINKING_MODELS = new Set(['gemini-3.1-flash-live-preview']);

// OpenAI Realtime 工具编排模式：
//   classic = 双 Agent，taskExecutor 委派 catalog-agent reasoning（缺省，向后兼容）
//   direct  = 单 Agent，voice agent 直连 MCP 工具
export const OPENAI_REALTIME_TOOL_MODES = ['classic', 'direct'] as const;
export type OpenAIRealtimeToolMode = (typeof OPENAI_REALTIME_TOOL_MODES)[number];
export const OPENAI_REALTIME_TOOL_MODE_DEFAULT: OpenAIRealtimeToolMode = 'classic';

// Realtime 応答言語モード（OpenAI 線のみ）：
//   fixed = 常に画面（SelectLanguage）で選択した言語で応答（缺省，向后兼容：与现状逐字节相同）
//   auto  = 各ターンのユーザー発話の言語に合わせて応答。画面の言語は判定不能時の既定
export const REALTIME_LANGUAGE_MODES = ['fixed', 'auto'] as const;
export type RealtimeLanguageMode = (typeof REALTIME_LANGUAGE_MODES)[number];
export const REALTIME_LANGUAGE_MODE_DEFAULT: RealtimeLanguageMode = 'fixed';

// provider 展示名。后续拓展只需在此和 REALTIME_VOICE_OPTIONS / REALTIME_DEFAULT_VOICE_MAP 中追加。
export const REALTIME_PROVIDER_LABEL: Record<RealtimeProvider, string> = {
  gemini: 'Gemini',
  openai: 'OpenAI',
};

export type RealtimeStatus = 'idle' | 'connecting' | 'listening' | 'speaking' | 'thinking' | 'processing';

/** taskExecutor 内 reasoning agent 的一次子工具调用（结果已按审计白名单裁剪，不含个人信息） */
export interface ReasoningSubCall {
  tool: string;
  arguments?: unknown;
  result?: unknown;
}

/**
 * 一次 OpenAI Realtime 工具调用的审计记录。随 turn-save 作为 assistant 消息的 tool_calls 入库
 * （后端存入 question_metadata），让履历能对账「模型实际执行了什么」，而不只是 ASR 转写文本。
 */
export interface RealtimeToolCallRecord {
  name: string;
  /** Realtime 模型生成的工具入参（task / query 等） */
  arguments: Record<string, unknown>;
  success: boolean;
  timestamp: number;
  /** taskExecutor：reasoning agent 实际调用的子工具及白名单结果 */
  subCalls?: ReasoningSubCall[];
  /** taskExecutor：reasoning agent 的最终答案（与最终播报对账用） */
  answer?: string;
  /** 其他结果元数据（rag_retrieval 的 total_count 等），已按白名单裁剪 */
  resultMeta?: Record<string, unknown>;
}

export interface RealtimeMessage {
  isUser: boolean;
  text: string;
  timestamp: number;
  /** 本条 assistant 消息之前执行的工具调用（仅最终答案消息携带） */
  toolCalls?: RealtimeToolCallRecord[];
}

export interface RealtimeHookConfig {
  botId: string;
  language?: string;
  voice?: string;
  userIdentifier?: string;
  voiceAgentPrompt?: string;
  /**
   * 会话级附加 prompt（可选）：Realtime 页开始通话前临时设置，追加在 bot 的 voice_agent_prompt 之后，仅本次通话生效、不落库。
   * OpenAI 链路由 buildRealtimePrompt 拼入 instructions；Gemini 链路在建连后经 config 消息交给 realtime 服务追加。
   */
  sessionPrompt?: string;
  onError?: (error: Error) => void;
  onToolCall?: (answer: string, toolArgs: { task: string }, toolName?: string) => void;
  onResponseDone?: (answer: string) => void;
}

export interface RealtimeHookReturn {
  status: RealtimeStatus;
  messages: RealtimeMessage[];
  currentStreamingMessage: RealtimeMessage | null;
  isConnected: boolean;
  error: string;
  isPaused: boolean;
  isMicMuted: boolean;
  isAudioPlaying: boolean;
  isStatusTimeout: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  clearMessages: () => void;
  pauseAudio: () => Promise<void>;
  resumeAudio: () => Promise<void>;
  pauseSession: () => Promise<void>;
  resumeSession: () => Promise<void>;
  muteInput: () => void;
  unmuteInput: () => void;
  sessionUsage?: RealtimeSessionUsage | null;
}
